import { gql } from "@apollo/client";
import { Fade } from "@material-ui/core";
import { CustomerPlanEnum } from "@resource/common";
import { useLogEvent } from "analytics";
import withPageAuthRequired from "auth/withPageAuthRequired";
import Layout from "components/Layout";
import { CreatePostTemplateDialog } from "components/PostTemplates/ManagementDialogs/CreatePostTemplateDialog";
import { DuplicatePostTemplateDialog } from "components/PostTemplates/ManagementDialogs/DuplicatePostTemplateDialog";
import { EditContentPostTemplateDialog } from "components/PostTemplates/ManagementDialogs/EditContentPostTemplateDialog";
import { EditSettingsPostTemplateDialog } from "components/PostTemplates/ManagementDialogs/EditSettingsPostTemplateDialog";
import PostTemplatesTable from "components/PostTemplates/PostTemplatesTable";
import { useCurrentQueryParamStateWithDefaults } from "components/Table/hooks/useQueryParamState";
import TemplatesLayout from "components/TemplatesLayout";
import {
  AllOrganizationPostTemplateIds,
  AllOrganizationPostTemplateIdsVariables,
  PostTemplateForEdit,
  PostTemplateForEditVariables,
  PostTemplatesFilterData,
  PostTemplatesPaginated,
  PostTemplatesPaginatedVariables,
  PostTemplateStatusEnum,
} from "generated/schemaTypes";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { formatEntity } from "shared/constants/entities";
import {
  usePushQueryParams,
  useQueryStringValue,
  useRemoveQueryParams,
} from "utils/next";
import { PermissionEnum } from "utils/permissions";
import useQuery from "utils/useQuery";

const FILTER_DATA = gql`
  query PostTemplatesFilterData {
    atsDepartments {
      id
      name
    }
  }
`;

const POST_TEMPLATES = gql`
  query PostTemplatesPaginated(
    $search: String
    $status: PostTemplateStatusEnum
    $departmentIds: [UUID!]
    $offset: Int
    $limit: Int
    $orderBy: [OrganizationPostTemplatesOrderByInput!]
  ) {
    currentOrganization {
      id
      name
      defaultAvatarImageUrl
      companyLogoUrl
      postTemplates(
        where: {
          search: $search
          status: $status
          departmentIds: $departmentIds
        }
        orderBy: $orderBy
        limit: $limit
        offset: $offset
      ) {
        id
        ...PostTemplatesTableRowFragment
      }
    }
  }
  ${PostTemplatesTable.fragments.postTemplate}
`;

const POST_TEMPLATE_FOR_EDIT = gql`
  query PostTemplateForEdit($id: ID!) {
    currentOrganization {
      id
      postTemplateById(id: $id) {
        id
        ...PostTemplatesTableRowFragment
      }
    }
  }
  ${PostTemplatesTable.fragments.postTemplate}
`;

const ALL_POST_TEMPLATES_IDS = gql`
  query AllOrganizationPostTemplateIds(
    $search: String
    $status: PostTemplateStatusEnum
    $departmentIds: [UUID!]
  ) {
    currentOrganization {
      id
      postTemplates(
        where: {
          search: $search
          status: $status
          departmentIds: $departmentIds
        }
      ) {
        id
      }
    }
  }
`;

function PostTemplates() {
  const initialPostTemplateId = useQueryStringValue("edit");
  const pushQueryParams = usePushQueryParams();
  const removeQueryParams = useRemoveQueryParams();
  const [selectedPostTemplateId, setSelectedPostTemplateId] = useState("");
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editContentDialogOpen, setEditContentDialogOpen] = useState(false);
  const [editSettingsDialogOpen, setEditSettingsDialogOpen] = useState(false);
  const logEvent = useLogEvent({
    component: "PostTemplatesPage",
    project: ["Zeus", "Post templates"],
  });

  useEffect(() => {
    if (
      initialPostTemplateId &&
      initialPostTemplateId !== selectedPostTemplateId
    ) {
      setSelectedPostTemplateId(initialPostTemplateId);
      setEditContentDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPostTemplateId]);

  const {
    pageIndex,
    pageSize,
    globalFilter: search,
    sortBy,
    filters,
  } = useCurrentQueryParamStateWithDefaults();
  const status =
    _.find(filters, {
      id: "status",
    })?.value ?? PostTemplateStatusEnum.ACTIVE;
  const departmentIds = _.find(filters, {
    id: "departmentIds",
  })?.value;

  const tableVariables = {
    limit: pageSize,
    offset: pageIndex * pageSize,
    search,
    status,
    departmentIds,
    orderBy: _(sortBy)
      .map(({ id, desc }) => ({ [id]: desc ? "desc" : "asc" }))
      .value(),
  };

  const fetchVariables = {
    ...tableVariables,
    orderBy: sortBy.map(({ id, desc }) => {
      let direction;
      if (id === "updatedAt") {
        direction = desc ? "asc" : "desc";
      } else {
        direction = desc ? "desc" : "asc";
      }
      return {
        [id]: direction,
      };
    }),
  };

  const { data: filterData } = useQuery<PostTemplatesFilterData>(FILTER_DATA);

  const {
    data,
    loading: isLoadingPostTemplates,
    error,
  } = useQuery<PostTemplatesPaginated, PostTemplatesPaginatedVariables>(
    POST_TEMPLATES,
    {
      variables: fetchVariables,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: allPostTemplateIdsData, loading: isLoadingAllPostTemplateIds } =
    useQuery<
      AllOrganizationPostTemplateIds,
      AllOrganizationPostTemplateIdsVariables
    >(ALL_POST_TEMPLATES_IDS, {
      variables: fetchVariables,
      fetchPolicy: "cache-and-network",
    });

  const postTemplates = useMemo(
    () => data?.currentOrganization?.postTemplates || [],
    [data?.currentOrganization?.postTemplates]
  );
  const allPostTemplateIds =
    _.map(allPostTemplateIdsData?.currentOrganization?.postTemplates, "id") ||
    [];

  const { data: editData } = useQuery<
    PostTemplateForEdit,
    PostTemplateForEditVariables
  >(POST_TEMPLATE_FOR_EDIT, {
    skip:
      !selectedPostTemplateId ||
      !!postTemplates.find((p) => p.id === selectedPostTemplateId),
    variables: {
      id: selectedPostTemplateId,
    },
  });
  const selectedPostTemplate = useMemo(
    () =>
      postTemplates.find((p) => p.id === selectedPostTemplateId) ||
      editData?.currentOrganization?.postTemplateById,
    [postTemplates, selectedPostTemplateId, editData]
  );
  const isLoading = isLoadingPostTemplates || isLoadingAllPostTemplateIds;

  if (error) {
    return (
      <p className="text-h4 text-dark">
        There was an unexpected error loading your {formatEntity("message")}{" "}
        templates, please refresh the page or contact support if the problem
        persists.
      </p>
    );
  }

  return (
    <>
      <Fade in timeout={1000}>
        <div className="mt-8">
          <PostTemplatesTable
            postTemplates={postTemplates}
            filterData={filterData}
            loading={isLoading}
            tableVariables={tableVariables}
            allIds={allPostTemplateIds}
            onRowClick={(postTemplate) => {
              setSelectedPostTemplateId(postTemplate.id);
              pushQueryParams({
                edit: postTemplate.id,
              });
              logEvent("Edit post template settings clicked");
              setEditContentDialogOpen(true);
            }}
            onDuplicate={(postTemplate) => {
              setSelectedPostTemplateId(postTemplate.id);
              setDuplicateDialogOpen(true);
            }}
            onCreateNew={() => {
              setSelectedPostTemplateId("");
              setCreateDialogOpen(true);
              logEvent("Create post template clicked", {
                customerSuccess: true,
              });
            }}
            onSettingsActionMenuItemClick={(postTemplate) => {
              logEvent("Edit post template settings clicked");
              setSelectedPostTemplateId(postTemplate.id);
              setEditSettingsDialogOpen(true);
            }}
          />
        </div>
      </Fade>
      {data?.currentOrganization && selectedPostTemplate && (
        <DuplicatePostTemplateDialog
          postTemplate={selectedPostTemplate}
          open={duplicateDialogOpen}
          setOpen={setDuplicateDialogOpen}
          organization={data.currentOrganization}
        />
      )}
      {data?.currentOrganization && (
        <CreatePostTemplateDialog
          organization={data.currentOrganization}
          open={createDialogOpen}
          setOpen={setCreateDialogOpen}
        />
      )}
      {selectedPostTemplate && (
        <EditSettingsPostTemplateDialog
          postTemplate={selectedPostTemplate}
          open={editSettingsDialogOpen}
          setOpen={setEditSettingsDialogOpen}
        />
      )}
      {data?.currentOrganization && selectedPostTemplate && (
        <EditContentPostTemplateDialog
          postTemplate={selectedPostTemplate}
          open={editContentDialogOpen}
          setOpen={(open: boolean) => {
            setEditContentDialogOpen(open);
            if (!open) {
              removeQueryParams(["edit"]);
            }
          }}
          organization={data.currentOrganization}
        />
      )}
    </>
  );
}

export default withPageAuthRequired(PostTemplates, {
  requiredPlan: CustomerPlanEnum.Platform,
  requiredPermissions: PermissionEnum.TEMPLATE_WRITE,
  getLayout: (page) => (
    <Layout>
      <TemplatesLayout>{page}</TemplatesLayout>
    </Layout>
  ),
});
